"use client";

import React from "react";
import { Table as AntTable, ConfigProvider, Checkbox } from "antd";
import type { TableProps as AntTableProps } from "antd";
import type { ColumnsType } from "antd/es/table";
import { orionTheme } from "../theme/orion-theme";
import clsx from "clsx";
import { DownOutlined } from "@ant-design/icons";
import { StatusPill } from "./StatusPill";
import "../styles/table.css";

export interface TableColumn {
  key: string;
  title: string;
  dataIndex: string;
  width?: number;
  sortable?: boolean;
  sortOrder?: "ascend" | "descend" | null;
  render?: (value: unknown, record: unknown, index: number) => React.ReactNode;
}

export interface TableRowSelection {
  selectedRowKeys: React.Key[];
  onChange: (selectedRowKeys: React.Key[], selectedRows: unknown[]) => void;
  getCheckboxProps?: (record: unknown) => { disabled?: boolean };
}

export interface TableProps
  extends Omit<AntTableProps, "columns" | "rowSelection"> {
  /**
   * Additional CSS classes (supports Tailwind overrides)
   */
  className?: string;

  /**
   * Table columns configuration
   */
  columns: TableColumn[];

  /**
   * Row selection configuration
   */
  rowSelection?: TableRowSelection;

  /**
   * Show header checkbox for select all
   */
  showHeaderCheckbox?: boolean;

  /**
   * Custom header title
   */
  headerTitle?: string;

  /**
   * Show sort indicators
   */
  showSortIndicators?: boolean;
}

/**
 * Table component - Ant Design Table with OpenStore Orion theming
 *
 * This component provides FULL access to all Ant Design Table props while
 * automatically applying the OpenStore Orion theme and Figma design specifications.
 *
 * Features:
 * - Orion design specifications (colors, spacing, typography, borders)
 * - Automatic theming
 * - Tailwind class override support
 * - ALL Ant Design Table features and props
 * - Custom row selection with checkboxes
 * - Sortable columns with indicators
 * - Status pills integration
 * - Product image support
 *
 * @example
 * ```tsx
 * // Basic table with Figma styling
 * <Table
 *   columns={columns}
 *   dataSource={data}
 *   rowSelection={{
 *     selectedRowKeys: selectedKeys,
 *     onChange: setSelectedKeys,
 *   }}
 *   showHeaderCheckbox
 * />
 *
 * // All Ant Design Table props work
 * <Table
 *   columns={columns}
 *   dataSource={data}
 *   pagination={{ current: 1, pageSize: 10 }}
 *   loading={isLoading}
 *   scroll={{ x: 800 }}
 *   size="small"
 *   bordered
 *   className="custom-table"
 * />
 * ```
 */
export const Table: React.FC<TableProps> = ({
  className,
  columns,
  rowSelection,
  showHeaderCheckbox = true,
  headerTitle,
  showSortIndicators = true,
  ...props
}) => {
  // Transform columns to include custom rendering
  const transformedColumns: ColumnsType = columns.map((column) => ({
    ...column,
    title:
      column.sortable && showSortIndicators ? (
        <div className="orion-table-header-sortable">
          <span>{column.title}</span>
          <DownOutlined className="orion-table-sort-icon" />
        </div>
      ) : (
        column.title
      ),
    sorter: column.sortable ? true : undefined,
    sortOrder: column.sortOrder,
  }));

  // Add selection column if rowSelection is provided
  if (rowSelection) {
    transformedColumns.unshift({
      title: showHeaderCheckbox ? (
        <Checkbox
          indeterminate={
            rowSelection.selectedRowKeys.length > 0 &&
            rowSelection.selectedRowKeys.length <
              (props.dataSource?.length || 0)
          }
          checked={
            rowSelection.selectedRowKeys.length === props.dataSource?.length &&
            props.dataSource?.length > 0
          }
          onChange={(e) => {
            if (e.target.checked) {
              const allKeys = props.dataSource?.map((_, index) => index) || [];
              rowSelection.onChange(
                allKeys,
                Array.from(props.dataSource || [])
              );
            } else {
              rowSelection.onChange([], []);
            }
          }}
          className="orion-table-header-checkbox"
        />
      ) : null,
      key: "selection",
      width: 50,
      render: (_, record, index) => (
        <Checkbox
          checked={rowSelection.selectedRowKeys.includes(index)}
          onChange={(e) => {
            const newSelectedKeys = e.target.checked
              ? [...rowSelection.selectedRowKeys, index]
              : rowSelection.selectedRowKeys.filter((key) => key !== index);

            const newSelectedRows = Array.from(props.dataSource || []).filter(
              (_, i) => newSelectedKeys.includes(i)
            );

            rowSelection.onChange(newSelectedKeys, newSelectedRows);
          }}
          className="orion-table-row-checkbox"
        />
      ),
    });
  }

  return (
    <ConfigProvider theme={orionTheme}>
      <div className={clsx("orion-table-container", className)}>
        {headerTitle && (
          <div className="orion-table-header-title">{headerTitle}</div>
        )}
        <AntTable
          className="orion-table"
          columns={transformedColumns}
          rowSelection={undefined} // We handle selection manually
          {...props}
        />
      </div>
    </ConfigProvider>
  );
};

Table.displayName = "Table";

// Export convenience components for common patterns
export const ProductTable: React.FC<
  Omit<TableProps, "columns"> & {
    showProductImage?: boolean;
    showStatus?: boolean;
    showInventory?: boolean;
    showSalesChannels?: boolean;
    showMarkets?: boolean;
    showCategory?: boolean;
    showType?: boolean;
    showVendor?: boolean;
  }
> = ({
  showProductImage = true,
  showStatus = true,
  showInventory = true,
  showSalesChannels = true,
  showMarkets = true,
  showCategory = true,
  showType = true,
  showVendor = true,
  ...props
}) => {
  const columns: TableColumn[] = [
    {
      key: "product",
      title: "Product",
      dataIndex: "product",
      width: 300,
      sortable: true,
      render: (value: unknown, record: unknown) => (
        <div className="orion-table-product-cell">
          {showProductImage && (record as any).image && (
            <img
              src={(record as any).image}
              alt={String(value)}
              className="orion-table-product-image"
            />
          )}
          <span className="orion-table-product-name">{String(value)}</span>
        </div>
      ),
    },
    ...(showStatus
      ? [
          {
            key: "status",
            title: "Status",
            dataIndex: "status",
            width: 100,
            sortable: true,
            render: (value: unknown) => (
              <StatusPill
                variant={String(value) === "active" ? "success" : "warning"}
              >
                {String(value) === "active" ? "Active" : "Draft"}
              </StatusPill>
            ),
          },
        ]
      : []),
    ...(showInventory
      ? [
          {
            key: "inventory",
            title: "Inventory",
            dataIndex: "inventory",
            width: 109,
            sortable: true,
            render: (value: unknown) => (
              <span className="orion-table-inventory">
                {String(value)} Variants
              </span>
            ),
          },
        ]
      : []),
    ...(showSalesChannels
      ? [
          {
            key: "salesChannels",
            title: "Sales Channels",
            dataIndex: "salesChannels",
            width: 143,
            sortable: true,
            render: (value: unknown) => (
              <span className="orion-table-sales-channels">
                {String(value)}
              </span>
            ),
          },
        ]
      : []),
    ...(showMarkets
      ? [
          {
            key: "markets",
            title: "Markets",
            dataIndex: "markets",
            width: 107,
            sortable: true,
            render: (value: unknown) => (
              <span className="orion-table-markets">{String(value)}</span>
            ),
          },
        ]
      : []),
    ...(showCategory
      ? [
          {
            key: "category",
            title: "Category",
            dataIndex: "category",
            width: 112,
            sortable: true,
            render: (value: unknown) => (
              <span className="orion-table-category">{String(value)}</span>
            ),
          },
        ]
      : []),
    ...(showType
      ? [
          {
            key: "type",
            title: "Type",
            dataIndex: "type",
            width: 145,
            sortable: true,
            render: (value: unknown) => (
              <span className="orion-table-type">{String(value)}</span>
            ),
          },
        ]
      : []),
    ...(showVendor
      ? [
          {
            key: "vendor",
            title: "Vendor",
            dataIndex: "vendor",
            width: 114,
            sortable: true,
            render: (value: unknown) => (
              <span className="orion-table-vendor">{String(value)}</span>
            ),
          },
        ]
      : []),
  ];

  return <Table columns={columns} {...props} />;
};

export const SimpleTable: React.FC<Omit<TableProps, "size">> = (props) => (
  <Table size="small" {...props} />
);

export const CompactTable: React.FC<Omit<TableProps, "size" | "pagination">> = (
  props
) => <Table size="small" pagination={false} {...props} />;
